/**
 * VibeCheck CLI v2 — Core Scanner Engine (Enterprise Edition)
 *
 * Comprehensive scanning for:
 *   - Hallucinations: ghost imports, phantom packages, fabricated APIs, unknown env vars
 *   - Security (OWASP Top 10): secrets, injection, XSS, SSRF, prototype pollution,
 *     eval injection, open redirect, CORS, regex DoS, timing attacks, insecure deserialization
 *   - Quality: console.log, debugger, empty catch, as any, TODOs, unused imports,
 *     long functions, deep nesting, complex conditionals, deprecated APIs, no-op code
 */

import * as fs from 'fs';
import * as path from 'path';
import type { ScanFinding, ScanCategory, SeverityLevel } from '../types';
import { readFileContent } from './file-walker';

let _findingId = 0;
function nextId(): string { return `VC-${++_findingId}`; }

// ── Main Scan ───────────────────────────────────────────────────────────────

export function scanFile(rootPath: string, filePath: string): ScanFinding[] {
  const content = readFileContent(rootPath, filePath);
  if (!content) return [];

  // Skip binary / minified / generated
  if (content.length > 500_000) return [];
  const avgLineLen = content.length / Math.max(content.split('\n').length, 1);
  if (avgLineLen > 500 && !filePath.endsWith('.env')) return []; // likely minified

  const findings: ScanFinding[] = [];
  const lines = content.split('\n');
  const ext = path.extname(filePath).toLowerCase();
  const isTest = /\.(test|spec)\.[jt]sx?$/.test(filePath) || filePath.includes('__tests__') || filePath.includes('__mocks__');
  const isCLI = filePath.includes('/bin/') || filePath.includes('/cli/') || filePath.includes('/commands/') || filePath.includes('/scripts/');
  const isConfig = /\.(config|rc)\.[jt]s$/.test(filePath) || filePath.endsWith('.config.js') || filePath.endsWith('.config.ts');

  const ctx: ScanContext = { rootPath, filePath, content, lines, ext, isTest, isCLI, isConfig };

  findings.push(...scanHallucinations(ctx));
  findings.push(...scanSecurity(ctx));
  findings.push(...scanQuality(ctx));

  return findings;
}

interface ScanContext {
  rootPath: string;
  filePath: string;
  content: string;
  lines: string[];
  ext: string;
  isTest: boolean;
  isCLI: boolean;
  isConfig: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// HALLUCINATION DETECTION — 7 rules (HAL001–HAL007)
// ═══════════════════════════════════════════════════════════════════════════

const KNOWN_FAKE_PACKAGES = new Set([
  'lodash/deepMerge', 'lodash/deepClone', 'lodash/flattenDeep',
  'react-native-web-hooks', 'next-auth/react-query',
  'express-validator/sanitize', 'mongoose/types',
  'stripe-helpers', 'aws-sdk-helpers', 'firebase-admin-helpers',
  'prisma-helpers', 'drizzle-helpers', 'supabase-helpers',
  '@types/express-session/memory', '@types/node/experimental',
  'react-server-components', 'next-api-middleware',
  'express-async-errors/handler', 'zod-prisma-types/client',
  '@vercel/ai-sdk', 'openai-helpers', 'langchain-helpers',
  'react-query/devtools', 'tanstack-query', '@tanstack/query',
  'next-auth/providers/credentials', 'firebase/admin',
  'supabase/auth', 'clerk/nextjs', 'auth0/nextjs',
]);

const SUSPICIOUS_IMPORT_PATTERNS = [
  /from\s+['"](@[a-z-]+\/[a-z-]+-helpers)['"]/,
  /from\s+['"](react-[a-z]+-[a-z]+-[a-z]+)['"]/,
  /from\s+['"](next-[a-z]+-[a-z]+)['"]/,
  /from\s+['"](express-[a-z]+-[a-z]+-[a-z]+)['"]/,
  /from\s+['"](vue-[a-z]+-[a-z]+-[a-z]+)['"]/,
  /from\s+['"](@[a-z-]+\/[a-z-]+-utils)['"]/,
];

const FAKE_CSS_PROPS = new Set([
  'text-stroke', 'backdrop-opacity', 'scroll-timeline',
  'container-name', 'text-wrap-balance', 'text-decoration-skip',
  'font-smooth', 'text-rendering-speed', 'layout-grid',
  'overflow-clip-margin-inline', 'border-boundary',
]);

const FABRICATED_API_PATTERNS = [
  { pattern: /fetch\s*\(\s*['"`]\/api\/v\d+\/(?:ai|ml|predict|generate|embed|classify|summarize|translate)['"`]/, name: 'AI/ML endpoint' },
  { pattern: /fetch\s*\(\s*['"`]https?:\/\/api\.(?:openai|anthropic|cohere)\.com\/v\d+\/(?!chat|completions|embeddings|models)/, name: 'Non-standard AI API path' },
  { pattern: /\.get\(\s*['"`]\/api\/(?:webhook|callback|notify)\/auto['"`]/, name: 'Auto-generated webhook' },
];

function scanHallucinations(ctx: ScanContext): ScanFinding[] {
  const findings: ScanFinding[] = [];
  if (!['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs'].includes(ctx.ext)) return findings;

  for (let i = 0; i < ctx.lines.length; i++) {
    const line = ctx.lines[i];
    const lineNum = i + 1;
    const trimmed = line.trim();

    // Skip comments
    if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*')) continue;

    // HAL001: Known fake package imports
    const importMatch = line.match(/(?:import|require)\s*\(?['"]([^'"]+)['"]\)?/);
    if (importMatch) {
      const pkg = importMatch[1];

      if (KNOWN_FAKE_PACKAGES.has(pkg)) {
        findings.push({
          id: nextId(), ruleId: 'HAL001', severity: 'high', category: 'hallucination',
          message: `Hallucinated import — '${pkg}' is not a real package`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed,
          fix: `Verify this package exists on npm: npm view ${pkg}. This looks like an AI-fabricated import.`,
        });
      }

      // HAL002: Ghost relative imports
      if (pkg.startsWith('./') || pkg.startsWith('../')) {
        const resolved = resolveRelativeImport(ctx.rootPath, ctx.filePath, pkg);
        if (!resolved) {
          findings.push({
            id: nextId(), ruleId: 'HAL002', severity: 'medium', category: 'hallucination',
            message: `Ghost import — '${pkg}' does not resolve to an existing file`,
            file: ctx.filePath, line: lineNum,
            snippet: trimmed,
            fix: `Check that the file exists. This import may have been hallucinated by AI.`,
          });
        }
      }
    }

    // HAL003: Suspicious import patterns
    for (const pattern of SUSPICIOUS_IMPORT_PATTERNS) {
      const match = line.match(pattern);
      if (match) {
        findings.push({
          id: nextId(), ruleId: 'HAL003', severity: 'low', category: 'hallucination',
          message: `Suspicious import — '${match[1]}' may not exist on npm`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed,
          fix: `Verify this package exists: npm view ${match[1]}`,
        });
      }
    }

    // HAL004: Fabricated CSS properties
    if (ctx.ext === '.tsx' || ctx.ext === '.jsx') {
      const cssMatch = line.match(/['"]?([\w-]+)['"]?\s*:\s*['"]?[\w-]+['"]?/);
      if (cssMatch && FAKE_CSS_PROPS.has(cssMatch[1])) {
        findings.push({
          id: nextId(), ruleId: 'HAL004', severity: 'low', category: 'hallucination',
          message: `Fabricated CSS property — '${cssMatch[1]}' is not a valid CSS property`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed,
          fix: `Check MDN for valid CSS properties. This may be AI-generated.`,
        });
      }
    }

    // HAL005: Unknown env vars
    const envMatch = line.match(/process\.env\.([A-Z_][A-Z0-9_]*)/);
    if (envMatch) {
      const envVar = envMatch[1];
      if (!isKnownEnvVar(ctx.rootPath, envVar)) {
        findings.push({
          id: nextId(), ruleId: 'HAL005', severity: 'medium', category: 'hallucination',
          message: `Unknown env var — '${envVar}' is not in .env or .env.example`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed,
          fix: `Add ${envVar} to .env.example or verify it's a real environment variable.`,
        });
      }
    }

    // HAL006: Fabricated API endpoints
    for (const { pattern, name } of FABRICATED_API_PATTERNS) {
      if (pattern.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'HAL006', severity: 'medium', category: 'hallucination',
          message: `Possibly fabricated ${name} — verify this endpoint exists`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Verify this API endpoint is real and documented. AI often fabricates plausible-looking endpoints.`,
        });
      }
    }

    // HAL007: Placeholder/skeleton code left by AI
    if (/\/\/\s*(?:TODO|FIXME):\s*(?:implement|add|replace|fill|complete)\s+(?:this|here|logic|code)/i.test(line) && !ctx.isTest) {
      const context3 = ctx.lines.slice(Math.max(0, i - 1), Math.min(ctx.lines.length, i + 3)).join('\n');
      if (/return\s+(?:null|undefined|true|false|\[\]|\{\}|''|""|0)\s*;/.test(context3)) {
        findings.push({
          id: nextId(), ruleId: 'HAL007', severity: 'medium', category: 'hallucination',
          message: `AI skeleton stub — TODO with placeholder return value`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 80),
          fix: `Implement the actual logic. This looks like an AI-generated stub that was never completed.`,
        });
      }
    }
  }

  return findings;
}

function resolveRelativeImport(rootPath: string, fromFile: string, importPath: string): boolean {
  const dir = path.dirname(path.resolve(rootPath, fromFile));
  const base = path.resolve(dir, importPath);
  const extensions = ['', '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs', '.json', '/index.ts', '/index.tsx', '/index.js', '/index.jsx', '/index.mjs'];
  return extensions.some(ext => fs.existsSync(base + ext));
}

let _envVarCache: Set<string> | null = null;
function isKnownEnvVar(rootPath: string, name: string): boolean {
  const builtins = new Set([
    'NODE_ENV', 'PORT', 'HOST', 'HOME', 'PATH', 'USER', 'SHELL', 'TERM', 'PWD', 'LANG',
    'CI', 'DEBUG', 'HOSTNAME', 'TZ', 'VERCEL', 'VERCEL_URL', 'VERCEL_ENV',
    'NEXT_PUBLIC_VERCEL_URL', 'NEXT_PUBLIC_VERCEL_ENV', 'NETLIFY', 'RAILWAY_ENVIRONMENT',
    'RENDER', 'FLY_APP_NAME', 'AWS_REGION', 'AWS_LAMBDA_FUNCTION_NAME',
    'GITHUB_ACTIONS', 'GITHUB_SHA', 'GITHUB_REF', 'GITHUB_REPOSITORY',
    'NEXT_RUNTIME', 'npm_package_version', 'npm_package_name',
  ]);
  if (builtins.has(name)) return true;
  if (name.startsWith('NEXT_PUBLIC_') || name.startsWith('VITE_') || name.startsWith('REACT_APP_') || name.startsWith('NUXT_')) return true;

  if (!_envVarCache) {
    _envVarCache = new Set<string>();
    for (const envFile of ['.env', '.env.local', '.env.example', '.env.development', '.env.production', '.env.test']) {
      const fp = path.join(rootPath, envFile);
      if (fs.existsSync(fp)) {
        const content = fs.readFileSync(fp, 'utf-8');
        for (const line of content.split('\n')) {
          const match = line.match(/^([A-Z_][A-Z0-9_]*)=/);
          if (match) _envVarCache.add(match[1]);
        }
      }
    }
  }
  return _envVarCache.has(name);
}

// ═══════════════════════════════════════════════════════════════════════════
// SECURITY SCANNING — OWASP Top 10 + Extended (SEC001–SEC020)
// ═══════════════════════════════════════════════════════════════════════════

const SECRET_PATTERNS: { pattern: RegExp; name: string; severity: SeverityLevel }[] = [
  // Cloud providers
  { pattern: /(?:AKIA|ABIA|ACCA|ASIA)[0-9A-Z]{16}/, name: 'AWS Access Key', severity: 'critical' },
  { pattern: /-----BEGIN (?:RSA |EC |DSA )?PRIVATE KEY-----/, name: 'Private Key', severity: 'critical' },
  // Git platforms
  { pattern: /ghp_[0-9a-zA-Z]{36}/, name: 'GitHub Personal Access Token', severity: 'critical' },
  { pattern: /gho_[0-9a-zA-Z]{36}/, name: 'GitHub OAuth Token', severity: 'critical' },
  { pattern: /ghs_[0-9a-zA-Z]{36}/, name: 'GitHub App Token', severity: 'critical' },
  { pattern: /github_pat_[0-9a-zA-Z_]{82}/, name: 'GitHub Fine-Grained PAT', severity: 'critical' },
  { pattern: /glpat-[0-9a-zA-Z\-_]{20,}/, name: 'GitLab Personal Access Token', severity: 'critical' },
  // AI providers
  { pattern: /sk-[a-zA-Z0-9]{20}T3BlbkFJ[a-zA-Z0-9]{20}/, name: 'OpenAI API Key (legacy)', severity: 'critical' },
  { pattern: /sk-proj-[a-zA-Z0-9\-_]{40,}/, name: 'OpenAI Project API Key', severity: 'critical' },
  { pattern: /sk-ant-api\d{2}-[a-zA-Z0-9\-_]{90,}/, name: 'Anthropic API Key', severity: 'critical' },
  // Payment
  { pattern: /sk_live_[0-9a-zA-Z]{24,}/, name: 'Stripe Secret Key', severity: 'critical' },
  { pattern: /pk_live_[0-9a-zA-Z]{24,}/, name: 'Stripe Publishable Key (live)', severity: 'high' },
  { pattern: /rk_live_[0-9a-zA-Z]{24,}/, name: 'Stripe Restricted Key', severity: 'critical' },
  { pattern: /sq0atp-[0-9A-Za-z\-_]{22}/, name: 'Square Access Token', severity: 'critical' },
  // Communication
  { pattern: /xox[bpas]-[0-9a-zA-Z-]{10,}/, name: 'Slack Token', severity: 'critical' },
  { pattern: /SG\.[0-9A-Za-z\-_]{22}\.[0-9A-Za-z\-_]{43}/, name: 'SendGrid API Key', severity: 'critical' },
  { pattern: /key-[0-9a-f]{32}/, name: 'Mailgun API Key', severity: 'critical' },
  // Auth
  { pattern: /[0-9]+-[0-9A-Za-z_]{32}\.apps\.googleusercontent\.com/, name: 'Google OAuth Client ID', severity: 'high' },
  // Database
  { pattern: /mongodb(\+srv)?:\/\/[^:]+:[^@]+@/, name: 'MongoDB Connection String with Credentials', severity: 'critical' },
  { pattern: /postgres(ql)?:\/\/[^:]+:[^@]+@/, name: 'PostgreSQL Connection String with Credentials', severity: 'critical' },
  { pattern: /mysql:\/\/[^:]+:[^@]+@/, name: 'MySQL Connection String with Credentials', severity: 'critical' },
  { pattern: /redis:\/\/[^:]*:[^@]+@/, name: 'Redis Connection String with Credentials', severity: 'critical' },
  // Generic
  { pattern: /(?:password|passwd|pwd)\s*[:=]\s*['"][^'"]{8,}['"]/, name: 'Hardcoded Password', severity: 'high' },
  { pattern: /(?:api[_-]?key|apikey)\s*[:=]\s*['"][a-zA-Z0-9]{16,}['"]/, name: 'Hardcoded API Key', severity: 'high' },
  { pattern: /(?:secret|token)\s*[:=]\s*['"][a-zA-Z0-9]{16,}['"]/, name: 'Hardcoded Secret/Token', severity: 'high' },
  // JWT
  { pattern: /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/, name: 'Hardcoded JWT Token', severity: 'high' },
];

const SQL_INJECTION_PATTERNS = [
  /(?:query|exec|execute|raw|rawQuery)\s*\(\s*[`'"].*\$\{/,
  /(?:query|exec|execute|raw)\s*\(\s*['"].*\+\s*(?:req|request|params|query|body)\./,
  /\.query\(\s*`[^`]*\$\{[^}]*(?:req|params|query|body)/,
  /\$\{.*\}\s*(?:WHERE|AND|OR|SET|VALUES|INSERT|UPDATE|DELETE|FROM|JOIN|ORDER|GROUP|HAVING)/i,
];

const XSS_PATTERNS = [
  /dangerouslySetInnerHTML\s*=\s*\{\s*\{\s*__html\s*:\s*(?!sanitize|DOMPurify|purify|escape)/,
  /\.innerHTML\s*=\s*(?!.*(?:sanitize|purify|escape|encode))/,
  /document\.write\s*\(/,
  /\.html\(\s*(?:req|request|params|query|body)\./,
  /\$\(\s*['"]</.test.toString() ? /\$\(\s*['"]</ : /\$\(\s*['"]</,
  /\.insertAdjacentHTML\s*\(\s*['"][^'"]+['"]\s*,\s*(?:req|request|params|query|body|user)/,
];

function scanSecurity(ctx: ScanContext): ScanFinding[] {
  const findings: ScanFinding[] = [];
  if (!['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs', '.py', '.go', '.rs', '.java', '.rb', '.env'].includes(ctx.ext)) return findings;

  // Skip .env.example — it's supposed to have placeholder values
  if (ctx.filePath.endsWith('.env.example') || ctx.filePath.endsWith('.env.sample')) return findings;

  for (let i = 0; i < ctx.lines.length; i++) {
    const line = ctx.lines[i];
    const lineNum = i + 1;
    const trimmed = line.trim();

    // Skip comments
    if (trimmed.startsWith('//') || trimmed.startsWith('#') || trimmed.startsWith('*') || trimmed.startsWith('/*')) continue;
    // Skip test assertions and mock data
    if (ctx.isTest && /(?:expect|assert|mock|stub|fake|fixture|describe|it\(|test\()/.test(trimmed)) continue;

    // SEC001: Hardcoded secrets
    for (const { pattern, name, severity } of SECRET_PATTERNS) {
      if (pattern.test(line)) {
        // Skip if in a comment or string that looks like a placeholder
        if (/(?:example|placeholder|your[_-]|xxx|changeme|replace[_-]|CHANGE_ME|TODO)/i.test(line)) continue;
        // Skip test files for non-critical secrets
        if (ctx.isTest && severity !== 'critical') continue;
        findings.push({
          id: nextId(), ruleId: 'SEC001', severity, category: 'security',
          message: `Hardcoded ${name} found in source code`,
          file: ctx.filePath, line: lineNum,
          snippet: redactSecret(trimmed, 80),
          fix: `Move to environment variables. Never commit secrets to source control. Use a .env file and add it to .gitignore.`,
        });
      }
    }

    // SEC002: SQL Injection (A03:2021)
    if (!ctx.isTest) {
      for (const pattern of SQL_INJECTION_PATTERNS) {
        if (pattern.test(line)) {
          // Skip if using parameterized query markers
          if (/\$\d+|:\w+|\?/.test(line) && !/\$\{/.test(line)) continue;
          findings.push({
            id: nextId(), ruleId: 'SEC002', severity: 'critical', category: 'security',
            message: `SQL Injection risk — user input interpolated into query string`,
            file: ctx.filePath, line: lineNum,
            snippet: trimmed.slice(0, 120),
            fix: `Use parameterized queries ($1, $2) or your ORM's built-in escaping. Never concatenate user input into SQL.`,
          });
          break;
        }
      }
    }

    // SEC003: XSS (A07:2021)
    for (const pattern of XSS_PATTERNS) {
      if (pattern.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC003', severity: 'high', category: 'security',
          message: `XSS vulnerability — unsanitized HTML injection`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 120),
          fix: `Sanitize with DOMPurify.sanitize() before rendering. Use textContent instead of innerHTML where possible.`,
        });
        break;
      }
    }

    // SEC004: Insecure randomness (A02:2021)
    if (/Math\.random\(\)/.test(line) && /(?:token|secret|key|password|hash|salt|nonce|id|session|csrf|otp|code)/i.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'SEC004', severity: 'high', category: 'security',
        message: `Insecure randomness — Math.random() used for security-sensitive value`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 100),
        fix: `Use crypto.randomBytes(), crypto.randomUUID(), or crypto.getRandomValues() for security-sensitive values.`,
      });
    }

    // SEC005: Path traversal (A01:2021)
    if (/(?:readFile|writeFile|createReadStream|createWriteStream|unlink|rmdir|readdir|stat|access|open|appendFile)\s*(?:Sync)?\s*\(.*(?:req|params|query|body|user|input)\./.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'SEC005', severity: 'high', category: 'security',
        message: `Path traversal risk — user input used in file system operation`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 120),
        fix: `Validate paths with path.resolve() + path.normalize(). Check the resolved path starts with your allowed base directory.`,
      });
    }

    // SEC006: eval() / Function() injection (A03:2021)
    if (!ctx.isTest && !ctx.isConfig) {
      if (/\beval\s*\(/.test(line) && !/\/\/.*eval/.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC006', severity: 'critical', category: 'security',
          message: `Code injection — eval() allows arbitrary code execution`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Never use eval(). Use JSON.parse() for data, or a sandboxed VM for dynamic code.`,
        });
      }
      if (/new\s+Function\s*\(/.test(line) && !/\/\/.*Function/.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC006', severity: 'high', category: 'security',
          message: `Code injection — new Function() allows arbitrary code execution`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Avoid new Function(). Use structured alternatives like a switch/case or lookup table.`,
        });
      }
    }

    // SEC007: SSRF (A10:2021)
    if (!ctx.isTest) {
      if (/(?:fetch|axios|got|request|http\.get|https\.get|urllib)\s*\(.*(?:req|params|query|body|user|input)\./.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC007', severity: 'high', category: 'security',
          message: `SSRF risk — user input used in outbound HTTP request URL`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 120),
          fix: `Validate and whitelist allowed URLs/domains. Block internal IPs (127.0.0.1, 10.x, 169.254.x, 192.168.x).`,
        });
      }
    }

    // SEC008: Prototype pollution
    if (/(?:__proto__|constructor\s*\[|Object\.assign\s*\(\s*\{\}\s*,\s*(?:req|body|params|query|input|user))/.test(line) && !ctx.isTest) {
      findings.push({
        id: nextId(), ruleId: 'SEC008', severity: 'high', category: 'security',
        message: `Prototype pollution risk — unsafe object merge with user input`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 120),
        fix: `Use Object.create(null) as base, or a safe merge library. Never spread user input directly into objects.`,
      });
    }

    // SEC009: Open redirect
    if (!ctx.isTest) {
      if (/(?:res\.redirect|window\.location|location\.href|location\.assign|location\.replace)\s*\(?\s*(?:req|params|query|body|user|input)\./.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC009', severity: 'medium', category: 'security',
          message: `Open redirect — user input controls redirect destination`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 120),
          fix: `Validate redirect URLs against a whitelist of allowed domains. Use relative paths where possible.`,
        });
      }
    }

    // SEC010: Missing CORS configuration
    if (/cors\(\s*\)/.test(line) && !ctx.isTest) {
      findings.push({
        id: nextId(), ruleId: 'SEC010', severity: 'medium', category: 'security',
        message: `Permissive CORS — cors() with no options allows all origins`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 100),
        fix: `Configure CORS with specific origins: cors({ origin: ['https://yourdomain.com'] })`,
      });
    }
    if (/['"]Access-Control-Allow-Origin['"]\s*[,:]\s*['"]\*['"]/.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'SEC010', severity: 'medium', category: 'security',
        message: `Wildcard CORS — Access-Control-Allow-Origin: * allows all origins`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 100),
        fix: `Restrict to specific origins. Wildcard CORS can expose your API to cross-origin attacks.`,
      });
    }

    // SEC011: Regex DoS (ReDoS)
    if (/new\s+RegExp\s*\(.*(?:req|params|query|body|user|input)/.test(line) && !ctx.isTest) {
      findings.push({
        id: nextId(), ruleId: 'SEC011', severity: 'medium', category: 'security',
        message: `ReDoS risk — user input used to construct regex`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 100),
        fix: `Escape user input with a regex-escape function, or use string methods (includes, startsWith) instead.`,
      });
    }

    // SEC012: Insecure deserialization
    if (/(?:JSON\.parse|deserialize|unserialize|pickle\.loads|yaml\.load)\s*\(.*(?:req|body|params|query|user|input)/.test(line) && !ctx.isTest) {
      if (!/schema|validate|zod|yup|joi|ajv|superstruct/.test(ctx.lines.slice(Math.max(0, i - 5), i + 5).join(' '))) {
        findings.push({
          id: nextId(), ruleId: 'SEC012', severity: 'medium', category: 'security',
          message: `Insecure deserialization — parsing user input without validation`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Validate parsed data with a schema (Zod, Yup, Joi) before using it. Never trust deserialized user input.`,
        });
      }
    }

    // SEC013: Timing attack on string comparison
    if (/===?\s*(?:req|params|query|body|user|input)\.\w*(?:token|secret|key|password|hash|apiKey|api_key)/i.test(line) && !ctx.isTest) {
      findings.push({
        id: nextId(), ruleId: 'SEC013', severity: 'medium', category: 'security',
        message: `Timing attack risk — string comparison on security-sensitive value`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 100),
        fix: `Use crypto.timingSafeEqual() for constant-time comparison of secrets, tokens, and API keys.`,
      });
    }

    // SEC014: Insecure cookie configuration
    if (/cookie\s*\(|setCookie|set-cookie/i.test(line) && !ctx.isTest) {
      const cookieContext = ctx.lines.slice(i, Math.min(ctx.lines.length, i + 5)).join(' ');
      if (!/httpOnly|HttpOnly|http_only/.test(cookieContext) && /(?:session|token|auth|jwt)/i.test(cookieContext)) {
        findings.push({
          id: nextId(), ruleId: 'SEC014', severity: 'medium', category: 'security',
          message: `Insecure cookie — session/auth cookie may be missing httpOnly, secure, or sameSite flags`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Set httpOnly: true, secure: true, sameSite: 'strict' on authentication cookies.`,
        });
      }
    }

    // SEC015: Disabled security features
    if (/(?:helmet|csp|csrf|xss)\s*\(\s*\{\s*(?:enabled|active)\s*:\s*false/.test(line) && !ctx.isTest) {
      findings.push({
        id: nextId(), ruleId: 'SEC015', severity: 'high', category: 'security',
        message: `Security feature disabled — a security middleware or header is explicitly turned off`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 100),
        fix: `Do not disable security features in production. If needed for development, use environment checks.`,
      });
    }

    // SEC016: Hardcoded IP / localhost in production code
    if (!ctx.isTest && !ctx.isConfig && !ctx.isCLI) {
      if (/['"`]https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0)(?::\d+)?/.test(line)) {
        if (!/(?:test|dev|development|local|example|mock|fixture|spec)/i.test(ctx.filePath)) {
          findings.push({
            id: nextId(), ruleId: 'SEC016', severity: 'low', category: 'security',
            message: `Hardcoded localhost URL in production code`,
            file: ctx.filePath, line: lineNum,
            snippet: trimmed.slice(0, 100),
            fix: `Use environment variables for URLs: process.env.API_URL or process.env.BASE_URL`,
          });
        }
      }
    }

    // SEC017: Command injection
    if (!ctx.isTest && !ctx.isCLI) {
      if (/(?:exec|execSync|spawn|spawnSync|execFile)\s*\(.*(?:req|params|query|body|user|input)\./.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC017', severity: 'critical', category: 'security',
          message: `Command injection — user input passed to shell execution`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 120),
          fix: `Never pass user input to exec/spawn. Use execFile with an args array, or validate input against a strict whitelist.`,
        });
      }
    }

    // SEC018: Missing rate limiting on auth endpoints
    if (/(?:\/login|\/signin|\/auth|\/register|\/signup|\/reset-password|\/forgot-password)/.test(line) && !ctx.isTest) {
      if (/(?:app|router)\.\s*(?:post|put)\s*\(/.test(line)) {
        const nearbyCode = ctx.lines.slice(Math.max(0, i - 10), Math.min(ctx.lines.length, i + 10)).join(' ');
        if (!/(?:rateLimit|rate-limit|throttle|limiter|brute)/i.test(nearbyCode)) {
          findings.push({
            id: nextId(), ruleId: 'SEC018', severity: 'medium', category: 'security',
            message: `Missing rate limiting on authentication endpoint`,
            file: ctx.filePath, line: lineNum,
            snippet: trimmed.slice(0, 100),
            fix: `Add rate limiting middleware (express-rate-limit, @nestjs/throttler) to auth endpoints to prevent brute-force attacks.`,
          });
        }
      }
    }

    // SEC019: Unvalidated file upload
    if (/(?:multer|formidable|busboy|multipart)/.test(line) && !ctx.isTest) {
      const uploadContext = ctx.lines.slice(i, Math.min(ctx.lines.length, i + 15)).join(' ');
      if (!/(?:fileFilter|allowedTypes|mimetype|content-type|maxFileSize|limits)/i.test(uploadContext)) {
        findings.push({
          id: nextId(), ruleId: 'SEC019', severity: 'medium', category: 'security',
          message: `Unvalidated file upload — no file type or size restrictions detected`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Add file type validation (fileFilter), size limits (limits.fileSize), and store uploads outside the web root.`,
        });
      }
    }

    // SEC020: Exposed error details in production
    if (!ctx.isTest && !ctx.isCLI) {
      if (/(?:res\.(?:json|send|status)\s*\(.*(?:err|error)\.(?:stack|message)|catch\s*\([^)]*\)\s*\{[^}]*res\..*(?:stack|message))/.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'SEC020', severity: 'low', category: 'security',
          message: `Error details exposed — stack traces or error messages sent to client`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 100),
          fix: `Return generic error messages to clients. Log detailed errors server-side only. Use NODE_ENV checks.`,
        });
      }
    }
  }

  return findings;
}

function redactSecret(line: string, maxLen: number): string {
  // Redact anything that looks like a secret value
  const redacted = line
    .replace(/((?:sk|pk|ghp|gho|ghs|glpat|SG|xox)[_\-a-zA-Z0-9]{6})[a-zA-Z0-9\-_]+/g, '$1***REDACTED***')
    .replace(/((?:password|secret|token|key|apikey|api_key)\s*[:=]\s*['"])[^'"]+/gi, '$1***REDACTED***');
  return redacted.slice(0, maxLen) + (redacted.length > maxLen ? '...' : '');
}

// ═══════════════════════════════════════════════════════════════════════════
// CODE QUALITY — 15 rules (QLT001–QLT015)
// ═══════════════════════════════════════════════════════════════════════════

function scanQuality(ctx: ScanContext): ScanFinding[] {
  const findings: ScanFinding[] = [];
  if (!['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs'].includes(ctx.ext)) return findings;

  for (let i = 0; i < ctx.lines.length; i++) {
    const line = ctx.lines[i];
    const lineNum = i + 1;
    const trimmed = line.trim();

    // QLT001: console.log in production code
    if (!ctx.isTest && !ctx.isCLI && /^\s*console\.log\s*\(/.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'QLT001', severity: 'low', category: 'quality',
        message: `console.log in production code`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 80),
        fix: `Remove console.log or replace with a structured logger (winston, pino, consola).`,
      });
    }

    // QLT002: Standalone debugger statement
    if (/^\s*debugger\s*;?\s*$/.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'QLT002', severity: 'medium', category: 'quality',
        message: `Debugger statement left in code`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed,
        fix: `Remove the debugger statement before committing.`,
      });
    }

    // QLT003: Empty catch blocks
    if (/catch\s*\([^)]*\)\s*\{\s*\}/.test(line) || (trimmed === '} catch {' && i + 1 < ctx.lines.length && ctx.lines[i + 1].trim() === '}')) {
      const nextLines = ctx.lines.slice(i, i + 3).join(' ');
      if (!/(?:ignore|fallback|optional|expected|skip|graceful|noop|intentional)/i.test(nextLines)) {
        findings.push({
          id: nextId(), ruleId: 'QLT003', severity: 'medium', category: 'quality',
          message: `Empty catch block — errors are silently swallowed`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed,
          fix: `Log the error or handle it explicitly. Add a comment like '// intentionally ignored' if deliberate.`,
        });
      }
    }

    // QLT004: Type assertion abuse: as any
    if (/\bas\s+any\b/.test(line) && !ctx.isTest) {
      findings.push({
        id: nextId(), ruleId: 'QLT004', severity: 'low', category: 'quality',
        message: `Type assertion 'as any' bypasses TypeScript safety`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 80),
        fix: `Use proper typing instead of 'as any'. Consider 'as unknown as TargetType' if a cast is truly needed.`,
      });
    }

    // QLT005: TODO/FIXME/HACK comments
    const todoMatch = trimmed.match(/\/\/\s*(TODO|FIXME|HACK|XXX|BUG|WARN)\b[:\s]*(.*)/i);
    if (todoMatch) {
      findings.push({
        id: nextId(), ruleId: 'QLT005', severity: 'info', category: 'quality',
        message: `${todoMatch[1].toUpperCase()}: ${todoMatch[2].trim() || '(no description)'}`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 80),
      });
    }

    // QLT007: @ts-ignore / @ts-nocheck without explanation
    if (/\/\/\s*@ts-(?:ignore|nocheck|expect-error)\s*$/.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'QLT007', severity: 'low', category: 'quality',
        message: `TypeScript suppression without explanation — add a reason comment`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed,
        fix: `Add a reason: // @ts-ignore — [why this is needed]`,
      });
    }

    // QLT008: Nested ternary (readability)
    if (/\?[^:]*\?[^:]*:[^:]*:/.test(line) && line.length > 60) {
      findings.push({
        id: nextId(), ruleId: 'QLT008', severity: 'low', category: 'quality',
        message: `Nested ternary — hard to read and maintain`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 80),
        fix: `Extract to a helper function or use if/else for clarity.`,
      });
    }

    // QLT009: console.error/warn without context
    if (!ctx.isTest && !ctx.isCLI && /console\.(?:error|warn)\s*\(\s*(?:err|error|e)\s*\)/.test(line)) {
      findings.push({
        id: nextId(), ruleId: 'QLT009', severity: 'low', category: 'quality',
        message: `Error logged without context — add a descriptive message`,
        file: ctx.filePath, line: lineNum,
        snippet: trimmed.slice(0, 80),
        fix: `Add context: console.error('Failed to [action]:', error) — helps with debugging.`,
      });
    }

    // QLT010: Hardcoded magic numbers
    if (!ctx.isTest && !ctx.isConfig) {
      const magicMatch = trimmed.match(/(?:===?|!==?|[<>]=?|timeout|delay|interval|retry|max|min|limit)\s*[:=,]\s*(\d{4,})\b/);
      if (magicMatch && !trimmed.startsWith('//') && !trimmed.startsWith('*')) {
        findings.push({
          id: nextId(), ruleId: 'QLT010', severity: 'low', category: 'quality',
          message: `Magic number ${magicMatch[1]} — extract to a named constant`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 80),
          fix: `const MEANINGFUL_NAME = ${magicMatch[1]}; — named constants are self-documenting.`,
        });
      }
    }

    // QLT011: Deprecated API usage
    const deprecatedPatterns: { pattern: RegExp; msg: string; replacement: string }[] = [
      { pattern: /\bsubstr\s*\(/, msg: 'String.substr() is deprecated', replacement: 'Use String.slice() or String.substring() instead.' },
      { pattern: /\b__defineGetter__\b/, msg: '__defineGetter__ is deprecated', replacement: 'Use Object.defineProperty() instead.' },
      { pattern: /\b__defineSetter__\b/, msg: '__defineSetter__ is deprecated', replacement: 'Use Object.defineProperty() instead.' },
      { pattern: /\bescape\s*\(/, msg: 'escape() is deprecated', replacement: 'Use encodeURIComponent() instead.' },
      { pattern: /\bunescape\s*\(/, msg: 'unescape() is deprecated', replacement: 'Use decodeURIComponent() instead.' },
      { pattern: /new\s+Buffer\s*\(/, msg: 'new Buffer() is deprecated and unsafe', replacement: 'Use Buffer.from(), Buffer.alloc(), or Buffer.allocUnsafe() instead.' },
    ];
    for (const { pattern, msg, replacement } of deprecatedPatterns) {
      if (pattern.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'QLT011', severity: 'low', category: 'quality',
          message: `Deprecated API — ${msg}`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 80),
          fix: replacement,
        });
      }
    }

    // QLT012: Promise without catch/await
    if (!ctx.isTest && /\.then\s*\(/.test(line) && !/\.catch\s*\(/.test(line)) {
      // Check next few lines for .catch
      const nextFew = ctx.lines.slice(i, Math.min(ctx.lines.length, i + 5)).join('\n');
      if (!/\.catch\s*\(/.test(nextFew) && !/await\s/.test(line)) {
        findings.push({
          id: nextId(), ruleId: 'QLT012', severity: 'medium', category: 'quality',
          message: `Unhandled promise — .then() without .catch() can cause unhandled rejections`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 80),
          fix: `Add .catch() handler or use async/await with try/catch.`,
        });
      }
    }

    // QLT013: Async function without await
    if (/^\s*(?:export\s+)?async\s+function\s+(\w+)/.test(line) || /^\s*(?:export\s+)?const\s+\w+\s*=\s*async/.test(line)) {
      // Look ahead for await in the function body (simple heuristic: next 30 lines or until closing brace)
      let depth = 0;
      let foundAwait = false;
      for (let j = i; j < Math.min(ctx.lines.length, i + 50); j++) {
        const l = ctx.lines[j];
        depth += (l.match(/\{/g) || []).length - (l.match(/\}/g) || []).length;
        if (/\bawait\b/.test(l)) { foundAwait = true; break; }
        if (depth <= 0 && j > i) break;
      }
      if (!foundAwait) {
        findings.push({
          id: nextId(), ruleId: 'QLT013', severity: 'low', category: 'quality',
          message: `Async function without await — unnecessary async wrapper`,
          file: ctx.filePath, line: lineNum,
          snippet: trimmed.slice(0, 80),
          fix: `Remove the async keyword if the function doesn't use await.`,
        });
      }
    }
  }

  // QLT006: Unused imports (simple heuristic)
  const importLines: { name: string; line: number }[] = [];
  for (let i = 0; i < ctx.lines.length; i++) {
    const match = ctx.lines[i].match(/import\s+(?:\{([^}]+)\}|(\w+))\s+from/);
    if (match) {
      const names = match[1] ? match[1].split(',').map(s => s.trim().split(' as ').pop()!.trim()) : [match[2]];
      for (const name of names) {
        if (name && name !== 'type' && name.length > 1) {
          importLines.push({ name, line: i + 1 });
        }
      }
    }
  }

  for (const imp of importLines) {
    const rest = ctx.lines.slice(imp.line).join('\n');
    const regex = new RegExp(`\\b${imp.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
    const matches = rest.match(regex);
    if (!matches || matches.length === 0) {
      findings.push({
        id: nextId(), ruleId: 'QLT006', severity: 'low', category: 'quality',
        message: `Unused import — '${imp.name}' is imported but never used`,
        file: ctx.filePath, line: imp.line,
        fix: `Remove the unused import to keep the code clean.`,
      });
    }
  }

  // QLT014: Long functions (>60 lines)
  if (!ctx.isTest) {
    let funcStart = -1;
    let funcName = '';
    let braceDepth = 0;
    for (let i = 0; i < ctx.lines.length; i++) {
      const line = ctx.lines[i];
      const funcMatch = line.match(/^\s*(?:export\s+)?(?:async\s+)?function\s+(\w+)/);
      const arrowMatch = line.match(/^\s*(?:export\s+)?const\s+(\w+)\s*=\s*(?:async\s+)?\(/);
      if ((funcMatch || arrowMatch) && funcStart === -1) {
        funcStart = i;
        funcName = (funcMatch?.[1] || arrowMatch?.[1]) ?? 'anonymous';
        braceDepth = 0;
      }
      if (funcStart >= 0) {
        braceDepth += (line.match(/\{/g) || []).length - (line.match(/\}/g) || []).length;
        if (braceDepth <= 0 && i > funcStart) {
          const funcLen = i - funcStart + 1;
          if (funcLen > 60) {
            findings.push({
              id: nextId(), ruleId: 'QLT014', severity: 'low', category: 'quality',
              message: `Long function '${funcName}' — ${funcLen} lines (recommended: <60)`,
              file: ctx.filePath, line: funcStart + 1,
              fix: `Break into smaller, focused functions. Long functions are harder to test and maintain.`,
            });
          }
          funcStart = -1;
        }
      }
    }
  }

  // QLT015: Deep nesting (>4 levels)
  if (!ctx.isTest) {
    for (let i = 0; i < ctx.lines.length; i++) {
      const line = ctx.lines[i];
      const indent = line.match(/^(\s*)/)?.[1].length ?? 0;
      const indentLevel = Math.floor(indent / 2); // assume 2-space indent
      if (indentLevel >= 5 && line.trim().length > 0 && !line.trim().startsWith('//') && !line.trim().startsWith('*')) {
        findings.push({
          id: nextId(), ruleId: 'QLT015', severity: 'low', category: 'quality',
          message: `Deep nesting (${indentLevel} levels) — extract to helper functions or use early returns`,
          file: ctx.filePath, line: i + 1,
          snippet: line.trim().slice(0, 80),
          fix: `Use guard clauses (early returns) or extract nested logic into separate functions.`,
        });
        // Skip ahead to avoid flooding
        while (i + 1 < ctx.lines.length && (ctx.lines[i + 1].match(/^(\s*)/)?.[1].length ?? 0) >= indent) i++;
      }
    }
  }

  return findings;
}

// ── Reset for testing ───────────────────────────────────────────────────────

export function resetScanner(): void {
  _findingId = 0;
  _envVarCache = null;
}
